import styled from 'styled-components';
import ContentWrapper from '../ContentWrapper';
import { Flex } from '@rebass/grid';
import { FormattedMessage } from 'react-intl';
import { useRouter } from 'next/router';

const Footer = () => {
    const aboutUrls: Record<string, string> = {
        nl: 'https://www.waterlandvanfriesland.nl/nl',
        en: 'https://www.waterlandvanfriesland.nl/en',
        de: 'https://www.waterlandvanfriesland.nl/de',
    };
    const generalUrls: Record<string, string> = {
        nl: 'https://www.waterlandvanfriesland.nl/nl/algemene-boekingsvoorwaarden',
        en: 'https://www.waterlandvanfriesland.nl/en/general-booking-conditions',
        de: 'https://www.waterlandvanfriesland.nl/de/allgemeine-buchungsbedingungen',
    };
    const faqUrls: Record<string, string> = {
        nl: 'https://www.waterlandvanfriesland.nl/nl/veelgestelde-vragen-zoek-en-boek',
        en: 'https://www.waterlandvanfriesland.nl/en/frequently-asked-questions-search-and-book',
        de: 'https://www.waterlandvanfriesland.nl/de/haufig-gestellte-fragen-suchen-und-buchen',
    };
    const privacyUrls: Record<string, string> = {
        nl: 'https://www.waterlandvanfriesland.nl/nl/privacybeleid',
        en: 'https://www.waterlandvanfriesland.nl/en/privacy-and-cookie-statement',
        de: 'https://www.waterlandvanfriesland.nl/de/datenschutz-und-cookie-erklarung',
    };

    const { locale } = useRouter();

    const currentLocale = locale ?? 'nl';
    return (
        <Container>
            <StyledContentWrapper>
                <Flex pt={[null, '3.2rem']}>
                    <FooterNav>
                        <FooterSection>
                            <FooterColumn>
                                <div>
                                    <FooterTitle>
                                        <FormattedMessage defaultMessage="Informatie" />
                                    </FooterTitle>
                                    <li>
                                        <a href={aboutUrls[currentLocale]}>
                                            <FormattedMessage defaultMessage="Over VVV Waterland van Friesland" />
                                        </a>
                                    </li>
                                    <li>
                                        <a href={generalUrls[currentLocale]}>
                                            <FormattedMessage defaultMessage="Algemene boekingsvoorwaarden" />
                                        </a>
                                    </li>
                                    <li>
                                        <a href={faqUrls[currentLocale]}>
                                            <FormattedMessage defaultMessage="Veelgestelde vragen" />
                                        </a>
                                    </li>
                                    <li>
                                        <a href={privacyUrls[currentLocale]}>
                                            <FormattedMessage defaultMessage="Privacy- en cookiebeleid" />
                                        </a>
                                    </li>
                                </div>
                            </FooterColumn>
                            <FooterColumn>
                                <div>
                                    <FooterTitle style={{ marginBottom: '0.6rem' }}>
                                        <FormattedMessage defaultMessage="Over ons" />
                                    </FooterTitle>
                                    <p>
                                        <FormattedMessage defaultMessage="Je boekt bij:" />{' '}
                                        <b>VVV Waterland van Friesland</b>
                                        <br />
                                        Midstraat 99, 8501 AH Joure <br />
                                        +31 (0) 513 - 250 450 <br />
                                        <a href="mailto:info@waterlandvanfriesland.nl">info@waterlandvanfriesland.nl</a>
                                    </p>
                                </div>
                            </FooterColumn>
                            <FooterColumn>
                                <div>
                                    <FooterTitle style={{ marginBottom: '1rem', marginLeft: '1rem' }}>
                                        <FormattedMessage defaultMessage="Zekerheid voor je vakantie" />
                                    </FooterTitle>
                                    <LogoWrapper>
                                        <div>
                                            <StyledImage src="/static/img/VVV.png" />
                                            <StyledImage width="12.4rem" src="/static/img/Allianz.png" />
                                        </div>
                                        <div>
                                            <StyledImage src="/static/img/iDeal.png" />
                                            <StyledImage src="/static/img/VISA.png" />
                                            <StyledImage src="/static/img/Mastercard.png" />
                                            <StyledImage src="/static/img/Bancontact.png" />
                                            <StyledImage width="8.4rem" src="/static/img/SEPA.png" />
                                        </div>
                                    </LogoWrapper>
                                </div>
                            </FooterColumn>
                        </FooterSection>
                    </FooterNav>
                </Flex>
            </StyledContentWrapper>
        </Container>
    );
};

export default Footer;

const StyledContentWrapper = styled(ContentWrapper)`
    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.m}) {
        padding: 0;
        margin-bottom: 0;
    }
`;

const Container = styled.div`
    margin-top: 2rem;
    background-color: #0372bd;
    padding-bottom: 8rem;
`;

const FooterNav = styled.nav`
    display: flex;
    width: 100%;

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.xl}) {
        flex-direction: column;
    }
`;

const FooterSection = styled.div`
    display: flex;
    color: white;
    width: 80%;
    margin-left: auto;
    margin-right: auto;

    @media screen and (max-width: ${({ theme }) => theme.mediaQueries.l}) {
        width: 100%;
        flex-direction: column;
        padding: ${({ theme }) => theme.spacing['50_Semi']} ${({ theme }) => theme.spacing['40_Standard']};
        border-bottom: 1px solid rgba(255, 255, 255, 0.3);
    }

    @media screen and (max-width: 350px) {
        padding: ${({ theme }) => theme.spacing['50_Semi']} ${({ theme }) => theme.spacing['20_Tiny']};
    }
`;

const FooterColumn = styled.div`
    width: 100%;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) and (max-width: ${({ theme }) =>
            theme.mediaQueries.l}) {
        text-align: center;
    }

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.l}) {
        .grid {
            display: grid;
            grid-template-columns: 20% 25% 40%;
        }
    }

    a {
        color: ${({ theme }) => theme.colors.neutral['0']};
        text-decoration: none;
        font-size: 16px;
    }
`;

const LogoWrapper = styled.div`
    margin-top: 1rem;
    display: grid;
    grid-template-rows: repeat(2, 35%);
    grid-template-columns: 34rem;

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) and (max-width: ${({ theme }) =>
            theme.mediaQueries.l}) {
        justify-content: center;
    }

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.xl}) {
        grid-template-rows: 35%;
        grid-template-columns: 18.8rem 34rem;
    }

    color: ${({ theme }) => theme.colors.neutral['0']};
`;

const StyledImage = styled.img<{ width?: string }>`
    ${({ width }) => (width ? `width: ${width}` : `width: 6.4rem`)};
    height: 8rem;
    object-fit: cover;
`;

const FooterTitle = styled.h2`
    color: ${({ theme }) => theme.colors.neutral['0']};
    margin-bottom: 3rem;
`;
